import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, Form, FieldArray } from 'formik';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { LeaderboardEventScores, useModifyLeaderboardScoresMutation } from 'api';
import { updateLeaderboardScores } from 'apollo/reducers';
import { LeaderboardPointsSchema } from 'features/admin/validations';
import { ILeaderboardScores } from 'features/admin/types';
import { useNotify } from 'hooks';

import EditableRow from './EditableRow';

interface IProps {
  id: string;
  eventScores: LeaderboardEventScores[];
}

const useStyles = makeStyles({
  editCell: {
    width: 120,
  },
  pointCell: {
    width: 100,
  },
});

const EditablePointTable: FC<IProps> = ({ id, eventScores }) => {
  const [editIndex, setEditIndex] = useState(-1);
  const { t } = useTranslation();

  const classes = useStyles();
  const notify = useNotify();
  const [modifyLeaderboardScore, { loading }] = useModifyLeaderboardScoresMutation({
    onError: notify.mutationError,
    onCompleted: () => setEditIndex(-1),
    update: (cache, mutationResult) => {
      updateLeaderboardScores(cache, mutationResult, id);
    },
  });

  function handleSubmit(values: ILeaderboardScores) {
    modifyLeaderboardScore({
      variables: { id, scores: values.eventScores },
    });
  }

  function renderRow(score: LeaderboardEventScores, index: number) {
    return (
      <EditableRow
        key={index}
        loading={loading}
        index={index}
        editIndex={editIndex}
        onChange={setEditIndex}
      />
    );
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleSubmit}
      initialValues={{ eventScores }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      validationSchema={LeaderboardPointsSchema}>
      {({ values }: FormikProps<ILeaderboardScores>) => (
        <Form>
          <FieldArray name="scores">
            {() => (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.editCell}>{t('common:edit')}</TableCell>
                    <TableCell>{t('common:action')}</TableCell>
                    <TableCell className={classes.pointCell}>{t('common:point_plural')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{values.eventScores.map(renderRow)}</TableBody>
              </Table>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};

export default EditablePointTable;
